// The device door (docs/08-app-design.md 5.3; app/art/welcome/PLAN.md
// sections 4.2, 4.3 and 5): a device that holds a sealed vault opens
// unlock-first, on the occupied /welcome and on /locked alike; only the
// lede differs. The device passphrase opens the sealed master inside the
// worker; a wrong one changes nothing on chain and says so, tied to the
// field. The way out is "Forget this device" (ForgetSheet), a quiet link,
// never at Unlock's weight; Create and Restore are not offered here.
// - the field is current-password, required and never autofocused: main
//   takes focus on a route change, and on a phone the keyboard would cover
//   the rest; Unlock stays mint, and an empty field is the browser's own
//   "fill out this field" rather than a greyed button;
// - the vault's short address appears once, in the identity pill;
// - while a forget is pending or blocked the form is gone: the data is
//   queued for deletion, which IndexedDB cannot withdraw, so there is
//   nothing to keep and nothing to open (R60); the only offer is to try
//   again (R59). The sheet closes the moment FORGET is pressed (R62).
// - focus never falls to the body: a wrong passphrase returns it to the
//   field (so its "Not unlocked." description is read), Unlock is only
//   aria-disabled while it works, the pending block takes focus when it
//   replaces the form and Try again when the blocked state appears;
// - a successful unlock replaces this entry with /vault, so Back from the
//   vault does not land on a second /vault (Welcome also redirects); only
//   while this door is still mounted and still the current screen. An
//   unlock that resolves after the door was left (Back or Forward remounts
//   a fresh door, whose own unlock may land first; or the user moved on)
//   neither navigates nor replaces the entry the user is on now;
// - "That passphrase does not unlock this device." only when the passphrase
//   really failed to decrypt (session.ts isPassphraseRefusal); any other
//   failure (a storage read, the worker, the ledger open, an unlock stopped
//   by a lock or forget) names what failed, never the passphrase, and does
//   not mark the field invalid. The three refusals of docs/18-one-fee-reserve.md
//   (a browser that cannot hold the fee key, a setup from another deployment,
//   a setup that changed during the unlock) are whole sentences that say
//   what to do, and are shown as they are.
// - the legacy escape hatch (docs/18 2.4, "A legacy device on a browser that
//   fails the probe"): after an unlock refused because this browser cannot
//   hold the fee key, on a device that stores an older fee reserve envelope
//   (`hasLegacyReserve`), the door also renders LegacySendOutForm, which
//   offers "Send the older reserve out". The form is mounted before its press
//   begins the operation, so StrictMode's remount cannot cancel it, and it
//   keeps one place in the tree, so the door hiding its passphrase form while
//   an operation runs (the form then stands in its place) never remounts it.
//   "Forget this device" stays available throughout. That refusal comes from
//   the capability probe, before the passphrase is used, so the field is
//   cleared; and while it is the last answer the Forget sheet is told this
//   browser cannot unlock (`legacyOffer`).
// - a move of the older reserve whose confirmation finished after the lock
//   (docs/18 section 6, "A lock after the send lets the confirmation finish
//   and shows the outcome"): the session keeps it as its 'sweep-outcome'
//   notice, and the door shows it above the passphrase form until it is
//   dismissed; Home shows it after the next unlock otherwise. Not while a
//   forget is pending or blocked, which clears it.

import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { FeeKeyUnsupported, SetupChanged, WrongDeployment, isPassphraseRefusal, session, useSession } from '../session';
import { hasLegacyReserve } from '../storage';
import { EntranceStatus } from './EntranceFrame';
import { ForgetSheet } from './ForgetSheet';
import { LegacySendOutForm } from './LegacySendOutForm';
import { SweepOutcomeNotice } from './OlderReserve';
import { Field, StatusLine, shortAddress } from './primitives';

/** The wrong-passphrase sentence: only for a passphrase that really failed to decrypt (isPassphraseRefusal). */
const WRONG_PASSPHRASE = 'That passphrase does not unlock this device. The vault itself is unaffected; your 24 words still open it anywhere.';

/** What the door says when an unlock failed for any reason other than the passphrase: what failed, plainly, and that the vault is untouched. */
function unlockFailure(e: unknown): string {
  // docs/18 2.4 and section 3: each says why and what to do; the vault was not touched and storage did not change
  if (e instanceof FeeKeyUnsupported || e instanceof WrongDeployment || e instanceof SetupChanged) return e.message;
  const raw = (e instanceof Error ? e.message : String(e)).trim();
  // session.ts I3: a lock or forget started while this unlock was opening, and won
  if (raw === 'locked') return 'Opening stopped because the app locked again meanwhile. The vault itself is unaffected; unlock again.';
  const detail = raw ? ` Details: ${raw}${/[.!?]$/.test(raw) ? '' : '.'}` : '';
  return `This device could not open its stored setup.${detail} The vault itself is unaffected; try again, or reload the page.`;
}

export function DeviceDoor({ lede, onForgotten }: { lede: string; /** After both databases are gone: /locked goes to /welcome; /welcome needs nothing, the session itself says the device is empty (and Welcome then focuses main). */ onForgotten?: () => void }) {
  const [location, navigate] = useLocation();
  const { info, forget: forgetState, notice } = useSession();
  const [passphrase, setPassphrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; passphrase: boolean } | null>(null);
  // Whether this door is still mounted, and the route it is on now: an unlock that resolves after the door was left
  // must not navigate (StrictMode's remount sets `live` again).
  const live = useRef(false);
  const here = useRef(location);
  here.current = location;
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const [sheet, setSheet] = useState(false);
  // docs/18 2.4: the offer appears after a FeeKeyUnsupported refusal on a legacy device; `legacyActive` while the form
  // holds an operation, which then stands in the passphrase form's place.
  const [legacyOffer, setLegacyOffer] = useState(false);
  const [legacyActive, setLegacyActive] = useState(false);
  // The last unlock was refused with FeeKeyUnsupported: this browser cannot unlock, which the Forget sheet says.
  const [feeKeyRefused, setFeeKeyRefused] = useState(false);
  // True from the moment the sheet's FORGET is pressed until the session has answered: the sheet closes at once, so
  // its "Keep it" cannot be offered over a deletion that is already being submitted (R62), and the pending line shows
  // straight away rather than after the lock's own storage reads.
  const [forgetting, setForgetting] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const pendingRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const phase = forgetState?.status === 'blocked' ? 'blocked' : forgetState?.status === 'pending' || forgetting ? 'pending' : 'form';
  // When a forget replaces the form, focus follows it: the trigger and the sheet are gone. Not on mount, where
  // App.tsx's route change owns focus.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const id = requestAnimationFrame(() => {
      if (phase === 'pending') pendingRef.current?.focus({ preventScroll: true });
      else if (phase === 'blocked') retryRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [phase]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!passphrase || busy) return;
    setBusy(true);
    setError(null);
    const at = here.current;
    try {
      await session.unlock(passphrase);
      setPassphrase('');
      setFeeKeyRefused(false);
      // Left meanwhile (Back or Forward to a fresh door, or the user moved on): the screen up now owns the history.
      if (live.current && here.current === at) navigate('/vault', { replace: true });
    } catch (e) {
      const wrong = isPassphraseRefusal(e);
      setError({ text: wrong ? WRONG_PASSPHRASE : unlockFailure(e), passphrase: wrong });
      const unsupported = e instanceof FeeKeyUnsupported;
      setFeeKeyRefused(unsupported);
      // docs/18 2.4: the probe refused before the passphrase was used, so the typed text is not kept in the field.
      if (unsupported) setPassphrase('');
      if (wrong && live.current) {
        field.current?.focus();
        field.current?.select();
      }
      // docs/18 2.4: only this refusal, and only on a device that stores an older envelope, offers the escape hatch.
      // The count reads no envelope; a failed read offers nothing.
      if (e instanceof FeeKeyUnsupported) {
        hasLegacyReserve().then(
          (has) => {
            if (has && live.current) setLegacyOffer(true);
          },
          () => {},
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const doForget = async () => {
    setSheet(false);
    setForgetting(true);
    try {
      if (await session.forget()) onForgotten?.();
    } finally {
      setForgetting(false);
    }
  };

  const retryForget = async () => {
    if (await session.forget()) onForgotten?.();
  };

  return (
    <>
      <h1>Welcome back.</h1>
      <p className="entry-lede">{lede}</p>
      {info ? (
        <p className="ident">
          <span className="glyph" aria-hidden="true">
            v
          </span>
          Vault <span className="mono">{shortAddress(info.address)}</span>
        </p>
      ) : null}
      {forgetState?.status === 'blocked' ? (
        <div className="door" tabIndex={-1}>
          <StatusLine state="failed" word="Not forgotten yet.">
            {forgetState.message} This device cannot be kept: the vault and its funds are untouched, and the 24 words open the vault anywhere.
          </StatusLine>
          <button type="button" className="cta cta-mint" ref={retryRef} onClick={() => void retryForget()}>
            Try again
          </button>
        </div>
      ) : phase === 'pending' ? (
        <div className="door" tabIndex={-1} ref={pendingRef}>
          <StatusLine state="neutral" word="Forgetting this device.">
            Closing the app&rsquo;s own connections and deleting its data.
          </StatusLine>
        </div>
      ) : (
        <>
          {/* docs/18 section 6: the move that finished after the lock; this slot is always present, so the legacy form below keeps its place */}
          {notice?.kind === 'sweep-outcome' ? <SweepOutcomeNotice outcome={notice} /> : null}
          {legacyActive ? null : (
            <form onSubmit={submit} className="door">
              <Field id="passphrase" label="Device passphrase">
                <input
                  ref={field}
                  id="passphrase"
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  aria-invalid={error?.passphrase ? 'true' : 'false'}
                  aria-describedby={error ? 'unlock-error' : undefined}
                />
              </Field>
              {error ? (
                <StatusLine state="failed" word="Not unlocked." id="unlock-error">
                  {error.text}
                </StatusLine>
              ) : null}
              <button type="submit" className="cta cta-mint" aria-disabled={busy ? 'true' : undefined} aria-busy={busy ? 'true' : undefined}>
                {busy ? 'Opening' : 'Unlock'}
              </button>
            </form>
          )}
          {/* Always this position among its siblings, so hiding the passphrase form never remounts it (its unmount cancels). */}
          {legacyOffer ? <LegacySendOutForm onActiveChange={setLegacyActive} /> : null}
          {legacyActive ? null : <p className="door-note">Lost the passphrase? Forget this device, then restore from your 24 words. Neither moves the vault&rsquo;s funds.</p>}
          <div className="forget-row">
            {/* A double-click's second click (detail 2) never opens the sheet: a double-clicked Send shrinks its form
                and can bring this row under the pointer. A deliberate click is detail 1, a key press 0. */}
            <button type="button" className="cta-quiet" onClick={(e) => e.detail < 2 && setSheet(true)}>
              Forget this device
            </button>
          </div>
        </>
      )}
      <EntranceStatus />
      <ForgetSheet open={sheet} onClose={() => setSheet(false)} onForget={() => void doForget()} legacyOffer={feeKeyRefused} />
    </>
  );
}
